import type { TenantDocumentTypeCreate, TenantDocumentTypeUpdate } from "@juro/shared";
import { getAuthToken } from "../lib/clerkAuth.js";

const DEV_USER_ID = "user_amano_akihiro";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": DEV_USER_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * 書類種別マスタ1件（組み込み7種＋カスタム種別を1系統で扱う。Phase 2 統合後）。
 * `is_builtin` が true の場合は削除不可・`doc_type`/`profession`/`kind` 値変更不可
 * （API・UI 両方でブロックする）。
 */
export type TenantDocumentType = {
  id: string;
  tenant_id: string;
  doc_type: string;
  label: string;
  profession: string | null;
  kind: "invoice" | "estimate" | "calc_sheet";
  withholding_eligible: boolean;
  seq_key: string;
  seq_prefix: string;
  is_builtin: boolean;
  display_order: number;
  created_at: string;
};

export const tenantDocumentTypesApi = {
  list: () => request<{ data: TenantDocumentType[]; total: number }>("/tenant-document-types"),
  /** 新規作成（kind='invoice' のみ許可。doc_type 省略時はサーバー側でラベルから生成する） */
  create: (body: TenantDocumentTypeCreate) =>
    request<TenantDocumentType>("/tenant-document-types", { method: "POST", body: JSON.stringify(body) }),
  update: (docType: string, body: TenantDocumentTypeUpdate) =>
    request<TenantDocumentType>(`/tenant-document-types/${docType}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** 論理削除。組み込み種別・使用中の種別は 422 で拒否される */
  remove: (docType: string) =>
    request<{ ok: boolean }>(`/tenant-document-types/${docType}`, { method: "DELETE" }),
};
